import React, { createContext, CSSProperties, KeyboardEvent, MouseEvent, useContext, useRef, useState } from "react";
import {
  offset,
  flip,
  shift,
  size,
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import classNames from "classnames";
import style from "./select.module.css";
import { MagnifyingGlassIcon } from "frontend/ui-components/picker/magnifying-glass-icon";
import { PortalID } from "frontend/modal/portal-types";

const selectContext = createContext({} as any);

type JustChildren = React.PropsWithChildren<unknown>;

export function Select({
  children,
  allowOpenOptions = true,
  selectedId,
  setSelectedId,
}: React.PropsWithChildren<{
  allowOpenOptions?: boolean;
  selectedId: string;
  setSelectedId: (value: string) => void;
}>) {
  // Floating popover setup
  const [openDropdown, setOpenDropdown] = useState(false);
  const padding = 50; // Used to detect if the dropdown is overflowing the screen.

  const { refs, context, floatingStyles } = useFloating({
    open: openDropdown,
    onOpenChange: (open: boolean) => allowOpenOptions && setOpenDropdown(open),
    placement: "bottom",
    middleware: [
      offset({ mainAxis: 10 }),
      flip({ padding }),
      shift(),
      size({
        padding,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
        },
      }),
    ],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  return (
    <selectContext.Provider
      value={{
        allowOpenOptions,
        refs,
        context,
        openDropdown,
        setOpenDropdown,
        getReferenceProps,
        getFloatingProps,
        floatingStyles,
        selectedId,
        setSelectedId,
      }}
    >
      {children}
    </selectContext.Provider>
  );
}

export function Trigger({
  children,
  rounded,
  customClass,
}: JustChildren & { rounded?: boolean; customClass?: string }) {
  const { getReferenceProps, refs, allowOpenOptions } = useContext(selectContext);
  return (
    <div
      className={classNames(
        customClass ?? style.trigger,
        rounded && style.rounded,
        allowOpenOptions ? style.enabled : style.disabled
      )}
      ref={refs.setReference}
      {...getReferenceProps()}
      tabIndex={0}
    >
      {children}
    </div>
  );
}

export function Arrow({ theme = "light" }: { theme?: "light" | "dark" }) {
  const { openDropdown } = useContext(selectContext);
  return (
    <div
      className={style.arrow}
      style={{
        transform: `rotate(${openDropdown ? -180 : 0}deg)`,
      }}
    >
      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 1 5 5M1 1l4 4" stroke={theme === "light" ? "#11355E" : "#fff"} strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function Popover({
  children,
  className,
  style: _style,
}: React.PropsWithChildren<{ className?: string; style?: CSSProperties }>) {
  const { context, openDropdown, refs, getFloatingProps, floatingStyles, allowOpenOptions } = useContext(selectContext);

  return (
    openDropdown &&
    allowOpenOptions && (
      <FloatingPortal id={PortalID.UiWidgets}>
        <FloatingFocusManager context={context} restoreFocus={true} returnFocus={true}>
          <div
            ref={refs.setFloating}
            className={className}
            style={{
              ...floatingStyles,
              width: `${refs.reference.current?.getBoundingClientRect().width}px`,
              ..._style,
            }}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    )
  );
}

export function Search({
  searchValue,
  setSearchValue,
  rounded,
  className,
}: {
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  rounded?: boolean;
  className?: string;
}) {
  return (
    <div className={classNames(style.searchContainer, rounded && style.rounded, className, style["dark"])}>
      <MagnifyingGlassIcon />
      <input
        type="text"
        placeholder={"Search"}
        autoFocus
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />
    </div>
  );
}

export function Option({ id, disabled, children }: React.PropsWithChildren<{ id: string; disabled?: boolean }>) {
  const { selectedId } = useContext(selectContext);
  return (
    <div
      tabIndex={0}
      role="option"
      aria-selected={id === selectedId}
      className={style.option}
      data-id={id}
      data-disabled={!!disabled}
      data-selected={id === selectedId}
      onFocus={(e) => {
        const options = e.currentTarget.closest('[role="listbox"]');
        if (options) {
          const allOptions = (options as HTMLElement).querySelectorAll("[data-id]");
          for (let i = 0; i < allOptions.length; i++) {
            (allOptions[i] as HTMLElement).tabIndex = -1;
          }
          e.currentTarget.tabIndex = 0;
        }
      }}
    >
      {children}
      {!!disabled && <div className={style.disabledOverlay} />}
    </div>
  );
}

export function OptionsList({ children }: JustChildren) {
  const { setSelectedId, setOpenDropdown } = useContext(selectContext);
  const ref = useRef<HTMLDivElement>(null);

  function onClick(e: MouseEvent) {
    const target: HTMLElement | null = e.target as HTMLElement;
    const option = target.closest('[data-id][data-disabled="false"]') as HTMLElement;
    if (option) {
      setSelectedId(option.dataset.id);
      setOpenDropdown(false);
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    // get the html element that was clicked
    const target: HTMLElement | null = e.target as HTMLElement;
    // climb up the DOM until we react Option element that is not disabled
    const option = target.closest('[data-id][data-disabled="false"]') as HTMLElement;
    if (option) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setSelectedId(option.dataset.id);
        setOpenDropdown(false);
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        step(+1);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpenDropdown(false);
    }
  }

  function step(delta: 1 | -1) {
    if (ref.current) {
      const enabledOptions = ref.current.querySelectorAll(`[data-id][data-disabled="false"]`);
      let j = 0;
      for (let i = 0; i < enabledOptions.length; i++) {
        const element = enabledOptions.item(i) as HTMLElement;
        if (element == document.activeElement) {
          j = (i + delta + enabledOptions.length) % enabledOptions.length;
          break;
        }
      }
      const element = enabledOptions.item(j) as HTMLElement;
      element.focus();
      element.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
  }

  return (
    <div
      // maybe aria-role="listbox"
      role="listbox"
      tabIndex={-1}
      style={{ overflowY: "auto", overflowX: "hidden" }}
      ref={ref}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}
